/* eslint-disable local/no-banned-color-classes -- Tag colour palette: each tag has a specific
   colour that isn't remapped in src/index.css, so explicit `dark:bg-*`/`dark:text-*` variants
   are intentional here. Do not use the semantic tokens — the whole point of these classes is
   to render distinct, named colours per tag in both light and dark mode. */
import {
  TagColorEnum,
  TagColorOrganisationTagFragment,
  TagColorTagFragment,
  TagTypeEnum,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { CheckIcon } from '@/modules/shared/icons/CheckIcon'
import { CloseIcon } from '@/modules/shared/icons/CloseIcon'
import { classNames } from '@/modules/shared/utils/classNames'
import { translateTag } from '@/modules/shared/utils/translateTag'

interface TagColorProps {
  tag: TagColorTagFragment | TagColorOrganisationTagFragment
  isActive?: boolean
  onClick?: () => void
  onRemove?: () => void
  showId?: boolean
}

type TagColorClasses = { main: string; active: string }

const TAG_COLOR_CLASSES: Partial<Record<TagColorEnum, TagColorClasses>> = {
  [TagColorEnum.Amber]: {
    main: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-100',
    active: 'border-amber-500 border',
  },
  [TagColorEnum.Black]: { main: 'bg-gray-900 text-white', active: 'border-gray-500 border' },
  [TagColorEnum.Cyan]: {
    main: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-800 dark:text-cyan-100',
    active: 'border-cyan-500 border',
  },
  [TagColorEnum.Emerald]: {
    main: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100',
    active: 'border-emerald-500 border',
  },
  [TagColorEnum.Fuchsia]: {
    main: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-800 dark:text-fuchsia-100',
    active: 'border-fuchsia-500 border',
  },
  [TagColorEnum.Gray]: {
    main: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100',
    active: 'border-gray-500 border',
  },
  [TagColorEnum.Green]: {
    main: 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100',
    active: 'border-green-500 border',
  },
  [TagColorEnum.Indigo]: {
    main: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-100',
    active: 'border-indigo-500 border',
  },
  [TagColorEnum.Lime]: {
    main: 'bg-lime-100 text-lime-800 dark:bg-lime-800 dark:text-lime-100',
    active: 'border-lime-500 border',
  },
  [TagColorEnum.Neutral]: {
    main: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-100',
    active: 'border-neutral-500 border',
  },
  [TagColorEnum.Pink]: {
    main: 'bg-pink-100 text-pink-800 dark:bg-pink-800 dark:text-pink-100',
    active: 'border-pink-500 border',
  },
  [TagColorEnum.Red]: {
    main: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
    active: 'border-red-500 border',
  },
  [TagColorEnum.Rose]: {
    main: 'bg-rose-100 text-rose-800 dark:bg-rose-800 dark:text-rose-100',
    active: 'border-rose-500 border',
  },
  [TagColorEnum.Sky]: {
    main: 'bg-sky-100 text-sky-800 dark:bg-sky-800 dark:text-sky-100',
    active: 'border-sky-500 border',
  },
  [TagColorEnum.Slate]: {
    main: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
    active: 'border-slate-500 border',
  },
  [TagColorEnum.Stone]: {
    main: 'bg-stone-100 text-stone-800 dark:bg-stone-800 dark:text-stone-100',
    active: 'border-stone-500 border',
  },
  [TagColorEnum.Teal]: {
    main: 'bg-teal-100 text-teal-800 dark:bg-teal-800 dark:text-teal-100',
    active: 'border-teal-500 border',
  },
  [TagColorEnum.Violet]: {
    main: 'bg-violet-100 text-violet-800 dark:bg-violet-800 dark:text-violet-100',
    active: 'border-violet-500 border',
  },
  [TagColorEnum.White]: {
    main: 'bg-white text-gray-800 border border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600',
    active: 'border-gray-500 border',
  },
  [TagColorEnum.Zinc]: {
    main: 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-100',
    active: 'border-zinc-500 border',
  },
}

export default function TagColor({ tag, isActive, onClick, onRemove, showId }: TagColorProps) {
  const color =
    tag.__typename === 'OrganisationTag'
      ? tag.color
      : tag.__typename === 'Tag' && tag.tagType === TagTypeEnum.Allergen
        ? TagColorEnum.Red
        : tag.color
  const label = translateTag(tag)
  const getColorClasses = (selectedColor: TagColorEnum | null): TagColorClasses => {
    if (!selectedColor) return { main: '', active: '' }
    return TAG_COLOR_CLASSES[selectedColor] ?? { main: '', active: '' }
  }

  return (
    <span
      className={classNames(
        'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium',
        getColorClasses(color).main,
        onClick && 'cursor-pointer',
        isActive && getColorClasses(color).active
      )}
      onClick={onClick}
    >
      {label} {showId && `- ${tag.id}`}
      {isActive && <CheckIcon className="size-4" />}
      {onRemove && <CloseIcon className="size-4 cursor-pointer" onClick={onRemove} />}
    </span>
  )
}
